import { useEffect, useRef, useState } from 'react'
import { Plane, ArrowRight, User } from '@/components/icons'
import { AirportDetailDrawer } from './AirportDetailDrawer'
import { AlertBadge } from '@/components/ui/alert-badge'
import { Card } from '@/components/ui/card'
import { cn, getInitials } from '@/lib/utils'
import { isFlightLogOpenForLeg } from '@@api/flightLog.shared'
import {
  formatFlightDuration,
  formatLocalTime,
  formatLocalDate,
  formatUtcOffsetLabel,
} from '@/lib/flight-utils'
import { getAirportByIcao } from '@/lib/airport-db'
import { useIsPilot } from '@/hooks/useIsPilot'
import { FlightLogCompactRow } from './FlightLogStatusCard'
import type { useTripGetById } from '@/lib/hooks/trip'

type Leg = NonNullable<ReturnType<typeof useTripGetById>['data']>['legs'][number]

interface TripFlightCarouselProps {
  legs: Leg[]
  onFlightClick: (flightId: string) => void
  disabled?: boolean
  /**
   * 'carousel' (default) is the touch horizontal snap layout. 'list' stacks the
   * flight cards full-width — used by the desktop trip detail view.
   */
  layout?: 'carousel' | 'list'
  /** Hide the pilot flight-log row (e.g. on draft trips, where logs don't exist yet) */
  showFlightLog?: boolean
  /** Swipe this leg's card into view (deep links: trip page opened with ?legId=…) */
  initialFlightId?: string
}

export function TripFlightCarousel({
  legs,
  onFlightClick,
  disabled = false,
  layout = 'carousel',
  showFlightLog = true,
  initialFlightId,
}: TripFlightCarouselProps) {
  const [activeIndex, setActiveIndex] = useState(0)
  const scrollerRef = useRef<HTMLDivElement>(null)
  const cardRefs = useRef<(HTMLDivElement | null)[]>([])

  useEffect(() => {
    const root = scrollerRef.current
    if (!root || legs.length <= 1) return

    const observer = new IntersectionObserver(
      (entries) => {
        const best = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0]
        if (!best) return
        const idx = cardRefs.current.findIndex((el) => el === best.target)
        if (idx !== -1) setActiveIndex(idx)
      },
      { root, threshold: [0.5, 0.75, 1.0] },
    )
    cardRefs.current.forEach((el) => el && observer.observe(el))
    return () => observer.disconnect()
  }, [legs.length])

  const scrollToCard = (i: number) =>
    cardRefs.current[i]?.scrollIntoView({
      behavior: 'smooth',
      inline: 'center',
      block: 'nearest',
    })

  // Swipe the deep-linked leg into view. Re-runs when legId changes in the URL
  // (tapping a card writes it), which is a no-op — the card is already centered.
  useEffect(() => {
    if (!initialFlightId) return
    const index = legs.findIndex((leg) => leg.id === initialFlightId)
    if (index <= 0) return
    // Wait a frame so card refs and layout are settled before scrolling.
    const frame = requestAnimationFrame(() => scrollToCard(index))
    return () => cancelAnimationFrame(frame)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFlightId])

  if (layout === 'list') {
    return (
      <div className={cn(disabled && 'opacity-70', 'flex flex-col gap-3')}>
        {legs.map((leg) => (
          <TripFlightRow
            key={leg.id}
            leg={leg}
            disabled={disabled}
            onClick={() => onFlightClick(leg.id)}
            showFlightLog={showFlightLog}
          />
        ))}
      </div>
    )
  }

  // A single flight doesn't need the snap scroller — render the card plainly at
  // full width. iOS WebKit can resolve the percentage basis against the padded
  // scroller and snap-center the oversized card, clipping it on both edges.
  if (legs.length === 1) {
    return (
      <div className={cn(disabled && 'opacity-70')}>
        <TripFlightCard
          leg={legs[0]}
          disabled={disabled}
          onClick={() => onFlightClick(legs[0].id)}
          showFlightLog={showFlightLog}
        />
      </div>
    )
  }

  return (
    <div className={cn(disabled && 'opacity-70', 'bg-transparent')}>
      <div
        ref={scrollerRef}
        className="-mx-(--content-gutter) -my-2 px-(--content-gutter) py-2 flex gap-3 overflow-x-auto snap-x snap-mandatory scroll-smooth [&::-webkit-scrollbar]:hidden [scrollbar-width:none]"
      >
        {legs.map((leg, i) => (
          <div
            key={leg.id}
            ref={(el) => {
              cardRefs.current[i] = el
            }}
            // Card width = viewport − 2·(gap + 8px peek), expressed against the
            // gutter-padded scroller, so ~8px of each neighbor shows at the edges.
            // min-w-0 overrides the flex item's min-width:auto — without it a card
            // whose content min-width exceeds the basis (busy crew/PAX row on a
            // narrow viewport) grows past its siblings and overflows the screen.
            className="snap-center min-w-0 shrink-0 basis-[calc(100%+2*var(--content-gutter)-40px)]"
          >
            <TripFlightCard
              leg={leg}
              disabled={disabled}
              onClick={() => onFlightClick(leg.id)}
              showFlightLog={showFlightLog}
            />
          </div>
        ))}
      </div>

      {legs.length > 1 && (
        <div className="flex justify-center gap-2 pt-4">
          {legs.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => scrollToCard(i)}
              aria-label={`Go to flight ${i + 1}`}
              className={cn(
                'size-1.5 rounded-full transition-colors',
                i === activeIndex ? 'bg-content-brand-bold' : 'bg-border-L3',
              )}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function AvatarStack({
  people,
  greyOut = false,
  ring = false,
  unknownCount = 0,
}: {
  // `acknowledged` is present on crew (flight-assignment acceptance) but not
  // PAX — the dot renders only when it's a boolean, so PAX stacks get none.
  people: { id: string; firstName: string; lastName: string; avatarColor: string; acknowledged?: boolean }[]
  greyOut?: boolean
  /** Surface-colored ring around each avatar (Figma desktop flight row). */
  ring?: boolean
  /** Unknown PAX on the leg — appends one gray user-icon avatar when > 0. */
  unknownCount?: number
}) {
  const maxVisible = 3
  const visible = people.slice(0, maxVisible)
  const remaining = people.length - maxVisible

  return (
    <div className="flex items-center pr-1">
      {visible.map((person) => (
        <div key={person.id} className="relative -mr-1">
          <div
            className={cn(
              'size-6 rounded-full flex items-center justify-center text-xs font-medium text-content-primary-inverse',
              ring && 'border-2 border-surface-L0',
              greyOut && 'grayscale',
            )}
            style={{ backgroundColor: person.avatarColor }}
          >
            {getInitials(person.firstName, person.lastName)}
          </div>
          {typeof person.acknowledged === 'boolean' && (
            <span
              className={cn(
                'absolute left-1/2 -translate-x-1/2 -bottom-[3px] size-[9px] rounded-full border-[1.5px] border-content-primary-white',
                person.acknowledged ? 'bg-content-positive-default' : 'bg-content-notice-bold',
                greyOut && 'grayscale',
              )}
            />
          )}
        </div>
      ))}
      {unknownCount > 0 && (
        <div className="relative -mr-1">
          <div
            className={cn(
              'size-6 rounded-full flex items-center justify-center bg-content-primary-subtle-mid',
              ring && 'border-2 border-surface-L0',
              greyOut && 'grayscale',
            )}
          >
            <User className="size-3.5 text-content-primary-inverse" />
          </div>
        </div>
      )}
      {remaining > 0 && (
        <div
          className={cn(
            'size-6 -mr-1 rounded-full flex items-center justify-center text-xs font-medium bg-surface-L2 text-content-primary-subtle',
            ring && 'border-2 border-surface-L0',
          )}
        >
          +{remaining}
        </div>
      )}
    </div>
  )
}

function isResponded(i: {
  booleanResponse: boolean | null
  textResponse: string | null
  numberResponse: number | null
}) {
  return (
    i.booleanResponse !== null ||
    i.textResponse !== null ||
    i.numberResponse !== null
  )
}

interface TripFlightCardProps {
  leg: Leg
  disabled: boolean
  onClick: () => void
  showFlightLog: boolean
}

// Bordered tail-number badge — aircraft is per-flight (Figma V1.4).
function AircraftTailBadge({ registration }: { registration: string }) {
  return (
    <span className="flex shrink-0 items-center gap-2 rounded border border-border-L2 px-2 py-1">
      <Plane className="size-4 text-content-brand-bold" />
      <span className="text-xs font-semibold text-content-brand-bold">{registration}</span>
    </span>
  )
}

// ICAO code as a nested tap target inside the flight card: opens the airport
// info drawer without triggering the card's own onClick. Stays tappable even
// when the card is disabled — airport info is read-only.
function AirportCodeButton({ icao, className }: { icao: string; className: string }) {
  const [isInfoOpen, setIsInfoOpen] = useState(false)
  return (
    <>
      <button
        type="button"
        className={cn(className, 'text-left active:opacity-70')}
        onClick={(e) => {
          e.stopPropagation()
          setIsInfoOpen(true)
        }}
      >
        {icao}
      </button>
      <AirportDetailDrawer
        icao={icao}
        isOpen={isInfoOpen}
        onClose={() => setIsInfoOpen(false)}
      />
    </>
  )
}

// Departs/Arrives cell. 'card' (mobile flight card): label, airport-local date,
// then both times on one line — "18:00 UTC 21:00 UTC+3" (Figma 12263-27426).
// 'row' (desktop flight row): label, large local time + "UTC+4", then the UTC
// time stacked below — no date line (Figma 12384-18397).
function DualTimeCell({
  label,
  dateTime,
  tz,
  className,
  variant = 'card',
}: {
  label: 'Departs' | 'Arrives'
  dateTime: string | Date
  tz?: string | null
  className?: string
  variant?: 'card' | 'row'
}) {
  if (variant === 'row') {
    return (
      <div className={cn('flex flex-col text-xs font-semibold', className)}>
        <span className="leading-4 text-content-primary-subtle">{label}</span>
        <span className="flex items-baseline gap-1 whitespace-nowrap">
          <span className="text-base leading-6 text-content-primary-bold">
            {formatLocalTime(dateTime, tz)}
          </span>
          <span className="text-[10px] leading-4 text-content-primary-subtle">
            {formatUtcOffsetLabel(dateTime, tz)}
          </span>
        </span>
        <span className="flex items-baseline gap-1 whitespace-nowrap leading-4">
          <span className="text-content-primary-bold">{formatLocalTime(dateTime)}</span>
          <span className="text-[10px] text-content-primary-subtle">UTC</span>
        </span>
      </div>
    )
  }
  return (
    <div className={cn('flex flex-col text-xs font-semibold leading-4', className)}>
      <span className="text-content-primary-subtle">{label}</span>
      <span className="text-content-primary-bold">
        {formatLocalDate(dateTime, tz)}
      </span>
      <span className="flex items-baseline gap-1 whitespace-nowrap text-content-primary-bold">
        <span>{formatLocalTime(dateTime)}</span>
        <span className="text-[10px] text-content-primary-subtle">UTC</span>
        <span>{formatLocalTime(dateTime, tz)}</span>
        <span className="text-[10px] text-content-primary-subtle">
          {formatUtcOffsetLabel(dateTime, tz)}
        </span>
      </span>
    </div>
  )
}

function TripFlightCard({ leg, disabled, onClick, showFlightLog }: TripFlightCardProps) {
  const { isPilot } = useIsPilot()
  const depAirport = getAirportByIcao(leg.departureAirportIcao)
  const arrAirport = getAirportByIcao(leg.arrivalAirportIcao)
  const hasCrew = leg.crew && leg.crew.length > 0
  const hasPax = (leg.passengers?.length ?? 0) > 0 || leg.unknownPaxCount > 0

  const tasks = leg.tasks ?? []
  const allItems = tasks.flatMap((t) => t.items ?? [])
  const total = allItems.length
  const completed = allItems.filter(isResponded).length
  // Read counts as submitted — it's the post-review state of a submitted task.
  const allSubmitted = tasks.length > 0 && tasks.every((t) => t.status !== 'Draft')

  return (
    <Card
      interactive={!disabled}
      onClick={disabled ? undefined : onClick}
      className={cn(disabled && 'cursor-default')}
    >
      <div className="flex flex-col gap-3">
          {/* Route + tail row */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex min-w-0 flex-col">
              <div className="flex items-center gap-2">
                <AirportCodeButton
                  icao={leg.departureAirportIcao}
                  className="text-xl font-bold leading-7 tracking-[-0.5px] text-content-primary-bold"
                />
                <ArrowRight className="size-3 shrink-0 text-content-primary-bold" />
                <AirportCodeButton
                  icao={leg.arrivalAirportIcao}
                  className="text-xl font-bold leading-7 tracking-[-0.5px] text-content-primary-bold"
                />
              </div>
              <div className="flex min-w-0 items-center gap-1 text-xs font-semibold leading-4 text-content-primary-subtle">
                {depAirport?.municipality && (
                  <span className="truncate">{depAirport.municipality}</span>
                )}
                {depAirport?.municipality && arrAirport?.municipality && (
                  <ArrowRight className="size-2 shrink-0" />
                )}
                {arrAirport?.municipality && (
                  <span className="truncate">{arrAirport.municipality}</span>
                )}
                <span className="whitespace-nowrap">
                  {(depAirport?.municipality || arrAirport?.municipality) && '• '}
                  {formatFlightDuration(leg.departureDateTime, leg.arrivalDateTime)}
                </span>
              </div>
            </div>
            {leg.aircraft?.registration && (
              <AircraftTailBadge registration={leg.aircraft.registration} />
            )}
          </div>

          {/* Departs / Arrives row */}
          <div className="grid grid-cols-2 border-t border-border-L2 pt-3">
            <DualTimeCell
              label="Departs"
              dateTime={leg.departureDateTime}
              tz={depAirport?.timezone}
            />
            <DualTimeCell
              label="Arrives"
              dateTime={leg.arrivalDateTime}
              tz={arrAirport?.timezone}
              className="border-l border-border-L1 px-[14px]"
            />
          </div>

          {/* Crew + PAX + tasks row (Figma 12391-43765) */}
          <div className="flex items-center gap-3 border-t border-border-L2 pt-3">
            <div className="flex shrink-0 items-center gap-2">
              <span className="text-content-primary-subtle text-xs font-semibold">Crew</span>
              {hasCrew ? (
                <AvatarStack people={leg.crew!} greyOut={disabled} ring />
              ) : (
                <AlertBadge variant="warning" />
              )}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <span className="text-content-primary-subtle text-xs font-semibold">PAX</span>
              {hasPax ? (
                <AvatarStack
                  people={leg.passengers ?? []}
                  greyOut={disabled}
                  ring
                  unknownCount={leg.unknownPaxCount}
                />
              ) : (
                <AlertBadge variant="warning" />
              )}
            </div>
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <FlightTasksProgress
                total={total}
                completed={completed}
                allSubmitted={allSubmitted}
              />
            </div>
          </div>

          {/* Flight log status — pilots only, never on drafts, and only once
              the leg has departed (or already has a log) */}
          {isPilot && showFlightLog && (isFlightLogOpenForLeg(leg) || leg.flightLog) && (
            <div className="border-t border-border-L2 pt-3">
              <FlightLogCompactRow
                lifecycle={leg.flightLog?.lifecycle ?? 'notStarted'}
                timestamp={leg.flightLog?.submittedAt ?? leg.departureDateTime}
              />
            </div>
          )}
      </div>
    </Card>
  )
}

// Progress bar + "N/M tasks" text (or "No tasks"). Fills the remaining width.
// Shared by the mobile flight card and the desktop horizontal row (Figma
// 12391-43765 / 12384-18397).
function FlightTasksProgress({
  total,
  completed,
  allSubmitted,
}: {
  total: number
  completed: number
  allSubmitted: boolean
}) {
  if (total === 0) {
    return (
      <span className="flex-1 whitespace-nowrap text-right text-xs font-semibold text-content-primary-subtle">
        No tasks
      </span>
    )
  }
  const pct = (completed / total) * 100
  // Green requires every task to actually be submitted — a fully-answered but
  // never-submitted Draft shows as partial, matching the trip-completion guard.
  const progressState: 'empty' | 'partial' | 'complete' =
    allSubmitted
      ? 'complete'
      : completed === 0
        ? 'empty'
        : 'partial'
  const fillClass =
    progressState === 'complete'
      ? 'bg-content-positive-default'
      : progressState === 'partial'
        ? 'bg-content-notice-bold'
        : null
  return (
    <>
      <div className="flex-1 h-1 rounded-full bg-border-L3 overflow-hidden">
        {fillClass && (
          <div
            className={cn('h-full rounded-full transition-[width]', fillClass)}
            style={{ width: `${pct}%` }}
          />
        )}
      </div>
      <span
        className={cn(
          'shrink-0 whitespace-nowrap text-right text-xs font-semibold tabular-nums',
          progressState === 'complete'
            ? 'text-content-positive-default'
            : 'text-content-primary-subtle',
        )}
      >
        {completed}/{total} tasks
      </span>
    </>
  )
}

// Desktop horizontal flight row (Figma desktop trip detail). Single line:
// departure → arrival → Departs | Arrives → tail → Crew → PAX → Tasks.
function TripFlightRow({ leg, disabled, onClick, showFlightLog }: TripFlightCardProps) {
  const { isPilot } = useIsPilot()
  const depAirport = getAirportByIcao(leg.departureAirportIcao)
  const arrAirport = getAirportByIcao(leg.arrivalAirportIcao)
  const hasCrew = leg.crew && leg.crew.length > 0
  const hasPax = (leg.passengers?.length ?? 0) > 0 || leg.unknownPaxCount > 0

  const tasks = leg.tasks ?? []
  const allItems = tasks.flatMap((t) => t.items ?? [])
  const total = allItems.length
  const completed = allItems.filter(isResponded).length
  // Read counts as submitted — it's the post-review state of a submitted task.
  const allSubmitted = tasks.length > 0 && tasks.every((t) => t.status !== 'Draft')

  return (
    <Card
      interactive={!disabled}
      onClick={disabled ? undefined : onClick}
      className={cn('p-3', disabled && 'cursor-default')}
    >
      {/* Fixed-width columns so every flight row's items line up vertically —
          each card is its own grid, so auto tracks would resolve per row and
          drift. Crew/PAX tracks fit label + 3 avatars + overflow chip; the
          tasks progress (minmax(0,1fr)) absorbs the remaining width. All
          columns left-aligned, 32px apart (Figma 12384-18397). */}
      <div className="grid items-center gap-x-8 grid-cols-[4.5rem_0.75rem_4.5rem_max-content_1px_max-content_7rem_8rem_9rem_minmax(0,1fr)]">
        {/* Departure airport */}
        <div className="flex min-w-0 flex-col items-start">
          <AirportCodeButton
            icao={leg.departureAirportIcao}
            className="text-xl font-bold leading-7 tracking-[-1px] text-content-primary-bold"
          />
          {depAirport?.municipality && (
            <span className="max-w-full truncate text-xs font-semibold leading-4 text-content-primary-subtle">
              {depAirport.municipality}
            </span>
          )}
        </div>
        {/* Route arrow */}
        <ArrowRight className="size-3 shrink-0 text-content-primary-subtle-mid" />
        {/* Arrival airport */}
        <div className="flex min-w-0 flex-col items-start">
          <AirportCodeButton
            icao={leg.arrivalAirportIcao}
            className="text-xl font-bold leading-7 tracking-[-1px] text-content-primary-bold"
          />
          {arrAirport?.municipality && (
            <span className="max-w-full truncate text-xs font-semibold leading-4 text-content-primary-subtle">
              {arrAirport.municipality}
            </span>
          )}
        </div>
        {/* Departs */}
        <DualTimeCell
          label="Departs"
          dateTime={leg.departureDateTime}
          tz={depAirport?.timezone}
          variant="row"
        />
        {/* Divider */}
        <span className="h-8 w-px justify-self-center bg-border-L1" />
        {/* Arrives */}
        <DualTimeCell
          label="Arrives"
          dateTime={leg.arrivalDateTime}
          tz={arrAirport?.timezone}
          variant="row"
        />

        {/* Aircraft tail */}
        <div className="flex min-w-0 items-center">
          {leg.aircraft?.registration && (
            <AircraftTailBadge registration={leg.aircraft.registration} />
          )}
        </div>

        {/* Crew */}
        <div className="flex min-w-0 items-center gap-2">
          <span className="text-xs font-semibold text-content-primary-subtle">Crew</span>
          {hasCrew ? (
            <AvatarStack people={leg.crew!} greyOut={disabled} ring />
          ) : (
            <AlertBadge variant="warning" />
          )}
        </div>

        {/* PAX */}
        <div className="flex min-w-0 items-center gap-2">
          <span className="text-xs font-semibold text-content-primary-subtle">PAX</span>
          {hasPax ? (
            <AvatarStack
              people={leg.passengers ?? []}
              greyOut={disabled}
              ring
              unknownCount={leg.unknownPaxCount}
            />
          ) : (
            <AlertBadge variant="warning" />
          )}
        </div>

        {/* Tasks progress — fills remaining width */}
        <div className="flex min-w-0 items-center gap-2">
          <FlightTasksProgress
            total={total}
            completed={completed}
            allSubmitted={allSubmitted}
          />
        </div>

      </div>

      {/* Flight log status — pilots only, never on drafts, and only once
          the leg has departed (or already has a log) */}
      {isPilot && showFlightLog && (isFlightLogOpenForLeg(leg) || leg.flightLog) && (
        <FlightLogCompactRow
          lifecycle={leg.flightLog?.lifecycle ?? 'notStarted'}
          timestamp={leg.flightLog?.submittedAt ?? leg.departureDateTime}
          className="mt-3"
        />
      )}
    </Card>
  )
}
